import type { PersonStatus } from '@sentra/contracts';
import { personStatusSchema } from '@sentra/contracts';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { NativeSelect } from '@/design/components/native-select';

/**
 * A person's situation, as a control on the amendment form.
 *
 * **It saves with the amendment rather than on its own**, which is why it lives here and not in a
 * section beside the form. `updatePersonRequestSchema` already carries `status` and the service
 * reads `input.status ?? existing.status`, so one PATCH carries the fields and the situation
 * together: one version, one revision, and the same two audit rows a separate request produced.
 * A second instant PATCH sitting next to an open form would leave that form holding a version
 * number the write had just superseded, so the operator's next Save would conflict with their own
 * previous act.
 *
 * **The options are the schema's**, so a status added to the contract appears here with no edit.
 */
export function StatusField({
  value,
  disabled,
  onChange,
}: Readonly<{
  value: PersonStatus;
  disabled: boolean;
  onChange: (next: PersonStatus) => void;
}>): ReactNode {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-medium" htmlFor="person-status">
        {t('person.form.statusLabel')}
      </label>
      <NativeSelect
        id="person-status"
        className="w-56"
        value={value}
        disabled={disabled}
        onChange={(event) => {
          const parsed = personStatusSchema.safeParse(event.target.value);
          if (parsed.success) {
            onChange(parsed.data);
          }
        }}
      >
        {personStatusSchema.options.map((status) => (
          <option key={status} value={status}>
            {t(`person.table.status_${status}`)}
          </option>
        ))}
      </NativeSelect>
    </div>
  );
}
